import { useRef, useState } from "react";
import { Upload, Loader2, FileText, Lock } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { MicButton } from "@/components/MicButton";

const MAX_BYTES = 15 * 1024 * 1024; // 15 MB

async function extractFromFile(file: File): Promise<string> {
  const name = file.name.toLowerCase();
  if (file.size > MAX_BYTES) throw new Error("File is too large (max 15 MB).");

  if (name.endsWith(".txt") || name.endsWith(".md") || file.type.startsWith("text/")) {
    return await file.text();
  }

  if (name.endsWith(".docx")) {
    const mammoth: any = await import("mammoth/mammoth.browser" as any);
    const buf = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer: buf });
    return result.value;
  }

  if (name.endsWith(".pdf")) {
    const pdfjs: any = await import("pdfjs-dist");
    // Use the bundled worker via a URL import so Vite handles it.
    const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
    const buf = await file.arrayBuffer();
    const doc = await pdfjs.getDocument({ data: buf }).promise;
    let text = "";
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map((it: any) => ("str" in it ? it.str : "")).join(" ") + "\n\n";
    }
    return text.trim();
  }

  throw new Error("Unsupported file. Use .txt, .md, .pdf, or .docx.");
}

export function SourceDropzone({
  value,
  onChange,
  disabled = false,
  lockedMessage,
  appendOnUpload = false,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  lockedMessage?: string;
  /**
   * When true, dropping/uploading a new file appends to the existing value
   * under a labeled "=== Source N: filename ===" header instead of replacing.
   * Default false (replace) — used for essay-draft inputs.
   */
  appendOnUpload?: boolean;
}) {
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [sourceCount, setSourceCount] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    if (disabled) return;
    setLoading(true);
    try {
      const text = await extractFromFile(file);
      if (!text.trim()) throw new Error("Couldn't read any text from that file.");
      if (appendOnUpload && value.trim()) {
        const nextIdx = (sourceCount || 1) + 1;
        const header = `\n\n=== Source ${nextIdx}: ${file.name} ===\n\n`;
        onChange(value.trimEnd() + header + text);
        setSourceCount(nextIdx);
        toast.success(`Added ${file.name} as Source ${nextIdx}`);
      } else {
        onChange(appendOnUpload ? `=== Source 1: ${file.name} ===\n\n${text}` : text);
        setSourceCount(1);
        toast.success(`Loaded ${file.name}`);
      }
      setFileName(file.name);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't read that file.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-3 space-y-2">
      <div
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) void handleFile(file);
        }}
        onClick={() => {
          if (disabled) {
            toast.error(lockedMessage ?? "Subscribe to upload more files.");
            return;
          }
          inputRef.current?.click();
        }}
        className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center transition ${
          disabled
            ? "cursor-not-allowed border-border bg-muted/40 opacity-70"
            : dragOver
            ? "cursor-pointer border-primary bg-primary/5"
            : "cursor-pointer border-border bg-background hover:border-primary/40"
        }`}
        aria-disabled={disabled}
      >
        {disabled ? (
          <Lock className="h-5 w-5 text-muted-foreground" />
        ) : loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        ) : fileName ? (
          <FileText className="h-5 w-5 text-primary" />
        ) : (
          <Upload className="h-5 w-5 text-muted-foreground" />
        )}
        <div className="text-sm">
          {disabled ? (
            <span className="font-medium text-muted-foreground">
              {lockedMessage ?? "Subscribe to upload more files"}
            </span>
          ) : loading ? (
            "Reading file…"
          ) : fileName ? (
            <>
              <span className="font-medium">{fileName}</span> loaded —{" "}
              {appendOnUpload ? "drop another to add it as a new source" : "drop another to replace"}
            </>
          ) : (
            <>
              <span className="font-medium text-foreground">
                {appendOnUpload ? "Drop a source file here" : "Drop a file here"}
              </span>{" "}
              <span className="text-muted-foreground">or click to upload</span>
            </>
          )}
        </div>
        <div className="text-xs text-muted-foreground">.txt · .md · .pdf · .docx (max 15 MB)</div>
        <input
          ref={inputRef}
          type="file"
          disabled={disabled}
          accept=".txt,.md,.pdf,.docx,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = "";
          }}
        />
      </div>

      <div className="relative">
        <Textarea
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            if (fileName) setFileName(null);
          }}
          readOnly={disabled}
          placeholder={
            disabled
              ? "Subscribe to paste or upload more content."
              : "…or paste the passage, chapter, or article here."
          }
          className={`min-h-[160px] pr-12 ${disabled ? "cursor-not-allowed bg-muted/40" : ""}`}
        />
        {!disabled && (
          <div className="absolute right-2 top-2">
            <MicButton
              label="your essay"
              value={value}
              onChange={(v) => {
                onChange(v);
                if (fileName) setFileName(null);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
